/**
 * Tasa de IVA — fuente única del IVA en el frontend.
 *
 * Round 2026-07-03 (pedido del owner): la tasa dejó de ser una constante
 * hardcodeada. Ahora es CONFIGURABLE desde /configuracion (ajustes
 * generales, clave `ivaPorcentaje`, default 19) y este módulo la carga
 * una vez por sesión vía `/config-options/general-settings` (mismo
 * singleton que el umbral de precio bajo). Mientras no cargue —o si el
 * GET falla— se usa el default 19% (fail-safe, nunca bloquea).
 *
 * Cualquier conversión neto ↔ con IVA en componentes nuevos DEBE usar
 * `getIvaRate()` / `getIvaFactor()` o los helpers de aquí en lugar de
 * hardcodear `0.19` / `1.19`.
 *
 * El backend usa el mismo ajuste vía `backend/src/utils/ivaRate.js`
 * (cache TTL 60 s + warm-up al boot). Ver docs/flujo-financiero.md §8.
 */
import { apiFetch } from "../services/apiClient";

export const DEFAULT_IVA_PORCENTAJE = 19;

// Tasa vigente como fracción (0.19). Mutable a nivel de módulo: los
// componentes la leen SYNC vía getIvaRate(); `loadIvaRate()` la actualiza
// desde el backend (App.tsx la dispara al autenticarse y al volver el foco
// a la pestaña). TTL: una pestaña abierta hace días no puede quedarse con
// una tasa vieja para siempre — el modal del calendario persiste los IVA
// que calcula el cliente, así que la convergencia importa.
const TTL_MS = 5 * 60_000;
let _rate = DEFAULT_IVA_PORCENTAJE / 100;
let _promise: Promise<number> | null = null;
let _loadedAt = 0; // epoch ms del último load EXITOSO (0 = nunca / falló)

/** Tasa de IVA vigente como FRACCIÓN (ej. 0.19). */
export function getIvaRate(): number {
  return _rate;
}

/** Factor con IVA (ej. 1.19). */
export function getIvaFactor(): number {
  return 1 + _rate;
}

/** Porcentaje vigente para display (ej. 19 → "IVA 19 %"). */
export function getIvaPorcentaje(): number {
  return Math.round(_rate * 100 * 100) / 100;
}

/**
 * Carga la tasa configurada desde el backend. Cacheada con TTL de 5 min:
 * dentro de la ventana reusa la promesa; vencida (o si la última carga
 * FALLÓ — una carga fallida nunca queda cacheada), re-fetchea. `force`
 * salta el cache (lo usa la pestaña de configuración al guardar).
 * Silenciosa: en error conserva el valor vigente (default 19%).
 */
export function loadIvaRate(force = false): Promise<number> {
  const fresh = _loadedAt > 0 && Date.now() - _loadedAt < TTL_MS;
  if (_promise && fresh && !force) return _promise;
  _promise = apiFetch<{ data?: { ivaPorcentaje?: number } }>(
    "/config-options/general-settings",
    { silent: true },
  )
    .then((res) => {
      const pct = res?.data?.ivaPorcentaje;
      if (typeof pct === "number" && Number.isFinite(pct) && pct >= 0 && pct <= 100) {
        _rate = pct / 100;
        _loadedAt = Date.now();
      }
      // res null (error silencioso) o payload sin el campo: _loadedAt queda
      // en su valor previo → el próximo loadIvaRate() reintenta.
      return _rate;
    })
    .catch(() => _rate);
  return _promise;
}

/**
 * Calcula el valor con IVA a partir del neto.
 * Redondea al peso entero — coincide con `Math.floor` del backend en
 * `helpers.js` para los cálculos de comisión, pero usa `round` aquí
 * porque para inputs UX queremos el comportamiento "natural" (50.5 → 51,
 * no 50). El backend hace su propio redondeo al persistir; el cliente
 * solo necesita una preview consistente.
 */
export function conIvaFromNeto(neto: number | null | undefined): number {
  return Math.round((Number(neto) || 0) * getIvaFactor());
}

/**
 * Calcula el neto a partir del valor con IVA.
 * `neto = conIva / factor` (factor = 1 + tasa vigente).
 */
export function netoFromConIva(conIva: number | null | undefined): number {
  const v = Number(conIva) || 0;
  return v ? Math.round(v / getIvaFactor()) : 0;
}
